<template>
  <div class="w-full">
    <label v-if="label" :for="id" class="block text-md font-medium text-gray-700 mb-2">
      {{ label }}
    </label>

    <input
      :id="id"
      :type="type"
      :placeholder="placeholder"
      :value="modelValue"
      :disabled="disabled"
      @input="handleInput"
      @blur="handleBlur"
      :class="[computedClasses, disabled ? '' : 'hover:border-gray-400']"
    />

    <p v-if="showErrorMessage && error" class="mt-1 text-xs text-red-600">
      {{ error }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, useAttrs } from 'vue'

defineOptions({
  inheritAttrs: false,
})

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  id: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    default: 'text',
  },
  placeholder: {
    type: String,
    default: '',
  },
  error: {
    type: String,
    default: '',
  },
  showErrorMessage: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  width: {
    type: String,
    default: 'w-96',
  },
  height: {
    type: String,
    default: 'h-10',
  },
  onBlur: {
    type: Function,
    default: null,
  },
  onChange: {
    type: Function,
    default: null,
  },
})

const emit = defineEmits(['update:modelValue'])
const attrs = useAttrs()

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleInput = (event: any) => {
  emit('update:modelValue', event.target.value)

  if (props.onChange) {
    props.onChange(event)
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleBlur = (event: any) => {
  if (props.onBlur) {
    props.onBlur(event)
  }
}

const computedClasses = computed(() => [
  attrs.class,
  props.width,
  props.height,
  'w-full',
  'px-4',
  'border',
  'rounded-md',
  'focus:ring-2',
  'focus:ring-main-hover',
  'focus:border-transparent',
  'outline-none',
  'transition-all',
  'hover:border-gray-400',
  'text-sm',
  'disabled:bg-gray-100',
  'disabled:cursor-default',
  'disabled:text-gray-500',
  {
    'border-red-500': props.error,
    'border-gray-300': !props.error,
  },
])
</script>
